import { useAmplitude } from '@risksmart-app/components/src/segment';

import { JobWatchError } from '@/components/ai-workflows/jobWatchError';
import type { AIWorkflowJobResult } from '@/components/ai-workflows/useAIWorkflowService.types';
import { useJobMonitor } from '@/components/ai-workflows/useJobMonitor';
import type { AIWorkflowJobResultWithStatus } from '@/components/ai-workflows/useWorkflowRequest';
import { useWorkflowRequest } from '@/components/ai-workflows/useWorkflowRequest';

export class GuardrailRejectedError extends Error {
  constructor() {
    super('Request blocked by content guardrails');
  }
}

export const GUARDRAIL_ERROR_CODE = 'guardrail_rejected';

/**
 * Pulls together the functionality required to invoke a workflow and monitor the
 * resultant job for updates.
 * @param workflowApiEndpoint To call to invoke the workflow
 * @param amplitudeEventName Name used in the Amplitude event
 */
export const useAIWorkflowService = <TState>(
  workflowApiEndpoint: string,
  amplitudeEventName: string
) => {
  const { invokeWorkflow } = useWorkflowRequest<TState>();
  const { watchJob } = useJobMonitor<TState>(30_000);
  const { logEvent } = useAmplitude();

  const monitorJobProgress = async (
    job: AIWorkflowJobResultWithStatus<TState> | null
  ): Promise<AIWorkflowJobResult<TState>> => {
    if (job) {
      if (job.status === 'failed') {
        if (job.errorCode === GUARDRAIL_ERROR_CODE) {
          throw new GuardrailRejectedError();
        }
        throw new Error(
          `Encountered an error from the workflow invocation. Error: ${job.error}`
        );
      } else if (job.status === 'completed') {
        return job;
      } else {
        return await watchJob(job.streamLocation);
      }
    } else {
      throw new Error(
        'Encountered an error on the server when trying to invoke the workflow - no job was created'
      );
    }
  };

  /**
   * Invokes the workflow
   */
  const runWorkflow = async (
    workflowBodyBuilder: () => unknown
  ): Promise<AIWorkflowJobResult<TState>> => {
    logEvent(`${amplitudeEventName} Invoked`, {
      Feature: amplitudeEventName,
    });

    const job = await invokeWorkflow(workflowApiEndpoint, workflowBodyBuilder);

    try {
      return await monitorJobProgress(job);
    } catch (e) {
      if (e instanceof JobWatchError && e.errorCode === GUARDRAIL_ERROR_CODE) {
        throw new GuardrailRejectedError();
      }

      throw e;
    }
  };

  return {
    runWorkflow,
  };
};
